// pages/Config/RouterEditPage.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageContainer from '../../../layouts/PageContainer';
import PageHeader from '../../../components/Nav/PageHeader';
import RouterForm from '../../../components/Config/RouterForm';
import { useConfig } from '../../../DataStore/ConfigContext';
import ajaxConfig from '../../../util/remote/ajaxConfig';

// helpers
const normalizeMac = (raw = '') =>
  raw.toUpperCase().replace(/[^0-9A-F]/g, '').match(/.{1,2}/g)?.join(':') || '';
const computeRouterName = (mac) => (mac ? `OPENFI-${mac}` : 'OPENFI-');

/** API/Context -> Form shape mapper */
const mapRouterToForm = (r = {}) => {
  const mac = normalizeMac(r.mac || '');
  return {
    type: (r.type || r.router_type || 'other').toLowerCase(),
    // always derive name from MAC
    name: computeRouterName(mac),
    site: r.site || '',
    notes: r.description || r.notes || '',
    model: r.model || '',
    serial: r.serial || '',
    ssid: r.SSID || r.ssid || 'Openfi Network',
    mac, // normalized
    wanIp: r.wan_ip ?? r.wanIp ?? '',
    apiPort: r.api_port ?? r.apiPort ?? 8728,
    sshPort: r.ssh_port ?? r.sshPort ?? 22,
    location: r.location || '',
    latitude: r.latitude || '',
    longitude: r.longitude || '',
    hotspotName: r.hotspot_name ?? r.hotspotName ?? 'hotspot1',
    radiusEnabled: String(r.radius_enabled ?? r.radiusEnabled ?? '1') === '1',
    active: String(r.status ?? r.active) === 'active' || r.active === true,
    portalVisible: String(r.is_visible ?? r.portalVisible ?? '1') === '1',
  };
};

export default function RouterEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedRouter } = useConfig();
  const [initialValues, setInitialValues] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedRouter) {
      navigate('/settings/routers');
      return;
    }
    setInitialValues(mapRouterToForm(selectedRouter));
    setLoading(false);
  }, [selectedRouter, navigate]);

  const handleSave = async (payload) => {
    // enforce invariants on update too
    const mac = normalizeMac(payload.mac || '');
    const forced = { ...payload, mac, name: computeRouterName(mac), id: selectedRouter?.id };

    const server_response = await ajaxConfig.updateRouter(forced);
    if (server_response.status === 'OK') {
      navigate('/settings/routers', {
        state: { flash: { type: 'success', msg: 'Router updated.' } },
      });
      return { ok: true };
    }
    return { ok: false, message: server_response.message || 'Failed to update router.' };
  };

  return (
    <PageContainer>
      <PageHeader
        title="Edit Router"
        breadcrumb={
          <>
            <li className="breadcrumb-item"><Link to="/settings">Settings</Link></li>
            <li className="breadcrumb-item"><Link to="/settings/routers">Routers</Link></li>
            <li className="breadcrumb-item active">Edit</li>
          </>
        }
      />

      <div className="container-fluid">
        {loading ? (
          <div className="spinner-border" />
        ) : (
          <RouterForm
            // still pass editingId so the form shows “Save Changes”, etc.
            editingId={selectedRouter?.id ?? (/^\d+$/.test(String(id)) ? Number(id) : id)}
            initialValues={initialValues}
            onSubmit={handleSave}
          />
        )}
      </div>
    </PageContainer>
  );
}
